<script setup>
/* Jahrgangsfilter der Kaderseite als EIN Auswahlfeld im Chip-Stil (vorher eine Reihe Chips): "All years (38) ⌄"
   bzw. "2009 (17) ⌄". Der Klick öffnet eine Liste mit allen Jahrgängen und ihrer Anzahl (Reihenfolge wie
   übergeben: "All years" oben, neueste Jahrgänge zuerst). Zahlen, Filterlogik und Wechselwirkung mit Suche und
   Position bleiben in usePlayers.js / domain/squadFilter.js — hier nur Anzeige und Bedienung.

   Bedienung: Klick oder Enter/Leertaste/Pfeil unten öffnet; Pfeil hoch/runter bewegt, Pos1/Ende springen,
   Enter/Leertaste wählt, Esc schließt und gibt den Fokus an den Knopf zurück, Tab schließt, Klick daneben
   schließt. Einträge sind 44 px hoch (Handy). aria-haspopup="listbox", aria-expanded am Knopf; die Liste ist
   role="listbox", der aktive Jahrgang aria-selected. */
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiChevronDown } from '@mdi/js';

const props = defineProps({
  /* 'all' + Jahrgänge, in Anzeigereihenfolge */
  keys: { type: Array, required: true },
  counts: { type: Object, default: () => ({}) },
});
const value = defineModel({ type: String, required: true });

const { t } = useI18n();
const open = ref(false);
const root = ref(null);
const button = ref(null);
const listEl = ref(null);
const cursor = ref(0);

const uid = `year-select-${Math.random().toString(36).slice(2, 8)}`;
const labelOf = (key) => `${key === 'all' ? t('squad.filterAllYears') : key} (${props.counts[key] || 0})`;
const currentLabel = computed(() => labelOf(value.value));
const active = computed(() => value.value !== 'all');

async function openMenu() {
  if (open.value) return;
  open.value = true;
  cursor.value = Math.max(0, props.keys.indexOf(value.value));
  await nextTick();
  listEl.value?.focus?.();
}
function closeMenu({ refocus = false } = {}) {
  if (!open.value) return;
  open.value = false;
  if (refocus) nextTick(() => button.value?.focus?.());
}
function toggle() {
  if (open.value) closeMenu({ refocus: true });
  else openMenu();
}
function choose(key) {
  value.value = key;
  closeMenu({ refocus: true });
}

function onButtonKey(e) {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    openMenu();
  }
}
function onListKey(e) {
  const last = props.keys.length - 1;
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      cursor.value = Math.min(last, cursor.value + 1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      cursor.value = Math.max(0, cursor.value - 1);
      break;
    case 'Home':
      e.preventDefault();
      cursor.value = 0;
      break;
    case 'End':
      e.preventDefault();
      cursor.value = last;
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      if (props.keys[cursor.value] !== undefined) choose(props.keys[cursor.value]);
      break;
    case 'Escape':
      e.preventDefault();
      e.stopPropagation();
      closeMenu({ refocus: true });
      break;
    case 'Tab':
      closeMenu();
      break;
    default:
  }
}

function onDocumentClick(e) {
  if (open.value && root.value && !root.value.contains(e.target)) closeMenu();
}
onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>

<template>
  <div ref="root" class="year-select">
    <button
      ref="button"
      type="button"
      class="btn ghost small year-trigger"
      :class="{ active }"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-controls="uid"
      :aria-label="`${$t('squad.filterYearLabel')}: ${currentLabel}`"
      @click="toggle"
      @keydown="onButtonKey"
    >
      <span class="year-label">{{ currentLabel }}</span>
      <v-icon :icon="mdiChevronDown" size="18" class="year-chevron" :class="{ flipped: open }" />
    </button>

    <ul
      v-if="open"
      :id="uid"
      ref="listEl"
      class="year-menu"
      role="listbox"
      tabindex="-1"
      :aria-label="$t('squad.filterYearLabel')"
      :aria-activedescendant="`${uid}-${cursor}`"
      @keydown="onListKey"
    >
      <li
        v-for="(key, i) in keys"
        :id="`${uid}-${i}`"
        :key="key"
        class="year-option"
        :class="{ selected: key === value, cursor: i === cursor, empty: !(counts[key] || 0) }"
        role="option"
        :aria-selected="key === value"
        @click="choose(key)"
        @mousemove="cursor = i"
      >
        <span>{{ labelOf(key) }}</span>
        <span v-if="key === value" class="year-check" aria-hidden="true">✓</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.year-select {
  position: relative;
  display: inline-flex;
}
.year-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
}
.year-chevron {
  transition: transform 0.14s;
}
.year-chevron.flipped {
  transform: rotate(180deg);
}
.year-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 12;
  min-width: 100%;
  width: max-content;
  max-width: 88vw;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
  padding: 5px;
  list-style: none;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 12px 40px var(--shadow-strong);
  outline: none;
}
.year-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  color: var(--muted);
  font-size: var(--fs-sm);
  cursor: pointer;
}
.year-option.cursor {
  background: var(--card-hover);
  color: var(--text);
}
.year-option.selected {
  color: var(--green);
}
.year-option.empty:not(.selected) {
  opacity: 0.55;
}
.year-menu:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .year-chevron {
    transition: none;
  }
}
</style>
